import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';

import { Invoice as InvoiceType } from '../../types/payment';

interface InvoiceItem {
  id?: string;
  variant_id: string;
  product_name: string;
  variant_name?: string;
  quantity: number;
  unit_price: number;
  hsn_code?: string;
  tax_rate: number;
}

interface Invoice extends Pick<InvoiceType, 'id' | 'invoice_number' | 'status'> {
  line_items?: InvoiceItem[];
  items?: InvoiceItem[];
}

interface SalesReturnItem {
  variant_id: string;
  quantity: number;
  unit_price: number;
  description?: string;
  hsn_code?: string;
}

interface SalesReturnDialogProps {
  open: boolean;
  invoice: Invoice;
  onClose: () => void;
  onSubmit: (returnData: {
    invoice_id: string;
    return_reason: string;
    return_description?: string;
    items: SalesReturnItem[];
  }) => Promise<void>;
}

const RETURN_REASONS = [
  { value: 'defective', label: 'Defective Product' },
  { value: 'wrong_item', label: 'Wrong Item Shipped' },
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'quality_issues', label: 'Quality Issues' },
  { value: 'other', label: 'Other' }
];

const SalesReturnDialog: React.FC<SalesReturnDialogProps> = ({
  open,
  invoice,
  onClose,
  onSubmit
}) => {
  const [returnReason, setReturnReason] = useState<string>('');
  const [returnDescription, setReturnDescription] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setReturnReason('');
      setReturnDescription('');
      setSelectedItems(new Map());
      setError(null);
    }
  }, [open]);

  const handleItemToggle = (item: InvoiceItem) => {
    const newSelected = new Map(selectedItems);
    if (newSelected.has(item.variant_id)) {
      newSelected.delete(item.variant_id);
    } else {
      newSelected.set(item.variant_id, 1); // Default to 1 item
    }
    setSelectedItems(newSelected);
  };

  const handleQuantityChange = (variantId: string, change: number) => {
    const newSelected = new Map(selectedItems);
    const currentQty = newSelected.get(variantId) || 0;
    const invoiceItems = invoice.items || invoice.line_items || [];
    const item = invoiceItems.find(i => i.variant_id === variantId);
    
    if (!item) return;

    const newQty = Math.max(1, Math.min(item.quantity, currentQty + change));
    newSelected.set(variantId, newQty);
    setSelectedItems(newSelected);
  };

  const handleQuantityInput = (variantId: string, value: string) => {
    const newSelected = new Map(selectedItems);
    const invoiceItems = invoice.items || invoice.line_items || [];
    const item = invoiceItems.find(i => i.variant_id === variantId);
    
    if (!item) return;

    const qty = parseInt(value) || 0;
    const validQty = Math.max(1, Math.min(item.quantity, qty));
    newSelected.set(variantId, validQty);
    setSelectedItems(newSelected);
  };

  const calculateReturnTotal = () => {
    let total = 0;
    const invoiceItems = invoice.items || invoice.line_items || [];
    selectedItems.forEach((qty, variantId) => {
      const item = invoiceItems.find(i => i.variant_id === variantId);
      if (item) {
        const unitPrice = Number(item.unit_price) || 0;
        const taxRate = Number(item.tax_rate) || 0;
        const itemTotal = unitPrice * qty;
        const taxAmount = itemTotal * (taxRate / 100);
        total += itemTotal + taxAmount;
      }
    });
    return total;
  };

  const handleSubmit = async () => {
    // Validation
    if (!returnReason) {
      setError('Please select a return reason');
      return;
    }

    if (selectedItems.size === 0) {
      setError('Please select at least one item to return');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const items: SalesReturnItem[] = [];
      const invoiceItems = invoice.items || invoice.line_items || [];
      selectedItems.forEach((qty, variantId) => {
        const invoiceItem = invoiceItems.find(i => i.variant_id === variantId);
        if (invoiceItem) {
          items.push({
            variant_id: variantId,
            quantity: qty,
            unit_price: invoiceItem.unit_price,
            description: `Return: ${invoiceItem.product_name}${invoiceItem.variant_name ? ` - ${invoiceItem.variant_name}` : ''}`,
            hsn_code: invoiceItem.hsn_code
          });
        }
      });

      await onSubmit({
        invoice_id: invoice.id,
        return_reason: returnReason,
        return_description: returnDescription || undefined,
        items
      });

      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create sales return');
    } finally {
      setLoading(false);
    }
  };

  const isItemSelected = (variantId: string) => selectedItems.has(variantId);
  const getItemQuantity = (variantId: string) => selectedItems.get(variantId) || 0;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      aria-labelledby="sales-return-dialog-title"
      aria-describedby="sales-return-dialog-description"
    >
      <DialogTitle id="sales-return-dialog-title">
        Create Sales Return - Invoice {invoice.invoice_number}
      </DialogTitle>
      <DialogContent id="sales-return-dialog-description">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {invoice.status !== 'paid' && invoice.status !== 'partial' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Note: This invoice is not yet paid. Returns are typically processed for paid invoices.
          </Alert>
        )}

        <FormControl fullWidth margin="normal" required>
          <InputLabel>Return Reason</InputLabel>
          <Select
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value)}
            label="Return Reason"
          >
            {RETURN_REASONS.map((reason) => (
              <MenuItem key={reason.value} value={reason.value}>
                {reason.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label="Return Description (Optional)"
          multiline
          rows={3}
          value={returnDescription}
          onChange={(e) => setReturnDescription(e.target.value)}
          placeholder="Provide additional details about the return..."
          inputProps={{ maxLength: 2000 }}
          helperText={`${returnDescription.length}/2000 characters`}
        />

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
          Select Items to Return
        </Typography>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Select</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Invoice Qty</TableCell>
                <TableCell align="right">Return Qty</TableCell>
                <TableCell align="right">Return Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(invoice.items || invoice.line_items || []).map((item) => {
                const selected = isItemSelected(item.variant_id);
                const returnQty = getItemQuantity(item.variant_id);
                const unitPrice = Number(item.unit_price) || 0;
                const taxRate = Number(item.tax_rate) || 0;
                const returnTotal = selected ? unitPrice * returnQty * (1 + taxRate / 100) : 0;

                return (
                  <TableRow key={item.variant_id} selected={selected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={() => handleItemToggle(item)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.product_name}
                        {item.variant_name && ` - ${item.variant_name}`}
                      </Typography>
                      {item.hsn_code && (
                        <Typography variant="caption" color="text.secondary">
                          HSN: {item.hsn_code}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      ₹{unitPrice.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">
                      {selected ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Tooltip title="Decrease">
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(item.variant_id, -1)}
                              disabled={returnQty <= 1}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <TextField
                            size="small"
                            value={returnQty}
                            onChange={(e) => handleQuantityInput(item.variant_id, e.target.value)}
                            type="number"
                            inputProps={{
                              min: 1,
                              max: item.quantity,
                              style: { textAlign: 'center', width: '60px' }
                            }}
                            sx={{ mx: 1 }}
                          />
                          <Tooltip title="Increase">
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(item.variant_id, 1)}
                              disabled={returnQty >= item.quantity}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {selected ? `₹${returnTotal.toFixed(2)}` : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {selectedItems.size > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="h6" align="right">
              Total Return Amount: ₹{calculateReturnTotal().toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary" align="right" display="block">
              (Including taxes)
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || selectedItems.size === 0 || !returnReason}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Creating...' : 'Create Sales Return'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesReturnDialog;
